import clsx from 'clsx';
import { Icon } from 'copilot-design-system';
import { useMemo } from 'react';

import { TextField } from 'src/components/TextField';
import { COPYABLE_FIELDS_TYPES } from 'src/constants';
import AddressInput from 'src/features/crm/properties/AddressInput';
import {
  EditableInput,
  PropertyFieldMappings,
} from 'src/features/crm/properties/EditableInput';
import {
  CustomFieldValue,
  CustomFieldValueData,
} from 'src/features/crm/types/type';
import PhoneNumberInput from 'src/legacy/components/Inputs/PhoneNumberInput';
import { MultiSelect } from 'src/legacy/components/MultiSelect';
import { NumberTextField } from 'src/legacy/components/TextField';
import {
  CustomFieldOption,
  CustomFieldType,
  MultiSelectCustomField,
} from 'src/store/clients/types';

export enum PropertyFieldType {
  ADDRESS = 'address',
  PHONE_NUMBER = 'phoneNumber',
  NUMBER = 'number',
  MULTI_SELECT = 'multiSelect',
  TEXT = 'text',
  URL = 'url',
}

interface Props {
  isEditing: boolean;
  onSetIsEditing: (isEditing: boolean) => void;
  validate: (val: string) => string | null;
  onUpdate: (val: string) => void;
  field: MultiSelectCustomField | CustomFieldOption;
  onSaveProperty: (
    property: MultiSelectCustomField,
    deletedOptionKey?: string,
  ) => Promise<void>;
  onFieldValueUpdate: (property: CustomFieldValueData) => void;
  fieldValue: CustomFieldValue;
}

export const getFieldInputComponent = (fieldType: CustomFieldType) => {
  switch (fieldType) {
    case PropertyFieldType.ADDRESS:
      return AddressInput;
    case PropertyFieldType.PHONE_NUMBER:
      return PhoneNumberInput;
    case PropertyFieldType.NUMBER:
      return NumberTextField;
    case PropertyFieldType.MULTI_SELECT:
      return MultiSelect;
    default:
      return TextField;
  }
};

export const CustomFieldWrapper = ({
  isEditing,
  field,
  fieldValue,
  onUpdate,
  validate,
  onSetIsEditing,
  onFieldValueUpdate,
  onSaveProperty,
}: Props) => {
  const CustomFieldInputComponent = useMemo(
    () => getFieldInputComponent(field.type),
    [field.type],
  );
  return (
    <div
      className={clsx('w-full items-baseline flex gap-1', {
        'hover:bg-secondary-hover rounded px-1': !isEditing,
      })}
    >
      {!isEditing && (
        <div className="h-4 w-4 p-0.5 box-border flex items-center justify-center relative top-[2px]">
          <Icon
            icon={PropertyFieldMappings[field.type].icon}
            className="h-3 w-3 text-light-gray"
          />
        </div>
      )}

      {COPYABLE_FIELDS_TYPES.includes(field.type) && (
        <EditableInput
          type={field.type}
          fieldValue={typeof fieldValue === 'string' ? fieldValue : ''}
          value={typeof fieldValue === 'string' ? fieldValue : ''}
          onUpdate={onUpdate}
          validate={validate}
          isEditing={isEditing}
          onSetIsEditing={onSetIsEditing}
        />
      )}

      {field.type === PropertyFieldType.ADDRESS && (
        <CustomFieldInputComponent
          field={field}
          value={typeof fieldValue === 'object' ? fieldValue : {}}
          placeholder={PropertyFieldMappings[field.type].placeholder}
          onSaveValue={onFieldValueUpdate}
        />
      )}
      {field.type === PropertyFieldType.MULTI_SELECT && (
        <CustomFieldInputComponent
          field={field}
          initialFieldValue={typeof fieldValue === 'object' ? fieldValue : []}
          placeholder={PropertyFieldMappings[field.type].placeholder}
          onSaveProperty={onSaveProperty}
          onSaveValue={onFieldValueUpdate}
        />
      )}
    </div>
  );
};
